import type { DragEndEvent } from "@dnd-kit/core";
import type { File } from "schemas/generated/file";
import type { FunnelStep } from "schemas/generated/funnelStep";

import React, { useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import AddPageSelectionPopover from "@/features/react-editor/components/AddPageSelectionPopover";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { successToast } from "@replo/design-system/components/alert/Toast";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import copy from "copy-to-clipboard";
import { GripVertical, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

import { transformStepToFile } from "../utils/react-flow-helpers";
import { FunnelPagePopover } from "./FunnelPagePopover";

type Props = {
  funnelId: string;
  updateFunnelStepOrder: ({
    projectId,
    updatedOrder,
  }: {
    projectId: string;
    updatedOrder: string[];
  }) => void;
};

function FunnelPagesMenu({ funnelId, updateFunnelStepOrder }: Props) {
  const projectId = useCurrentProjectId();
  const [pageSelectionPopoverOpen, setPageSelectionPopoverOpen] =
    useState(false);
  const navigate = useNavigate();
  const { data: funnel } = trpc.funnel.find.useQuery(
    projectId && funnelId ? { projectId, funnelId } : skipToken,
  );

  const [steps, setSteps] = useOverridableState(funnel?.funnelSteps ?? []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Add delay and tolerance to allow click events to fire before drag
      activationConstraint: {
        delay: 100,
        tolerance: 15,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id && projectId) {
      const updatedSteps = arrayMove(
        steps,
        steps.findIndex((item: FunnelStep) => item.id === active.id),
        steps.findIndex((item: FunnelStep) => item.id === over.id),
      );

      setSteps(updatedSteps);
      updateFunnelStepOrder({
        projectId: projectId,
        updatedOrder: updatedSteps.map((step) => step.id),
      });
    }
  };

  if (!projectId) {
    return null;
  }

  return (
    <div className="h-full flex flex-col bg-white gap-3 rounded-lg overflow-y-auto w-[260px]">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={steps.map((step) => step.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="overflow-y-auto gap-3 flex flex-col">
            {steps.map((step, index) => (
              <PageMenuNode
                key={step.id}
                id={step.id}
                data={transformStepToFile({ step, index })}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <AddPageSelectionPopover
        isOpen={pageSelectionPopoverOpen}
        onOpenChange={setPageSelectionPopoverOpen}
        onSelectExistingPage={() => navigate("./page/new?menu-type=existing")}
        onSelectNewPage={() => navigate("./page/new?menu-type=new")}
      >
        <button
          type="button"
          className="bg-sheet border border-border h-[52px] border-dashed hover:bg-surface flex items-center gap-1 w-full justify-center rounded-lg transition-colors text-sm font-medium"
          onClick={() => setPageSelectionPopoverOpen(true)}
        >
          <Plus size={14} />
          Add Page
        </button>
      </AddPageSelectionPopover>
    </div>
  );
}

const PageMenuNode = ({ id, data }: { id: string; data: Partial<File> }) => {
  const { pageRoute, name, id: pageId } = data;

  const handleCopyName = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (name) {
      copy(name);
      successToast("Page name copied to clipboard");
    }
  };

  const handleCopyRoute = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (pageRoute) {
      copy(pageRoute);
      successToast("Page route copied to clipboard");
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="border-0.5 border-border flex flex-wrap rounded-lg flex-col h-fit p-2 bg-white w-full relative cursor-move"
    >
      <div className="flex flex-row w-full items-center gap-3">
        <div className="p-2">
          <GripVertical size={14} className="text-placeholder" />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <Tooltip content={name} triggerAsChild UNSAFE_className="flex">
            <div
              className="text-default text-base font-semibold truncate cursor-pointer hover:opacity-80 transition-colors"
              onClick={handleCopyName}
            >
              {name}
            </div>
          </Tooltip>

          <Tooltip content={pageRoute} triggerAsChild UNSAFE_className="flex">
            <div
              className="text-sm text-muted truncate cursor-pointer hover:opacity-80 transition-colors"
              onClick={handleCopyRoute}
            >
              {pageRoute}
            </div>
          </Tooltip>
        </div>
        <div
          className="ml-auto"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
        >
          {pageId && pageRoute && name && (
            <FunnelPagePopover
              pageData={{ pageId, pageRoute, name }}
              funnelStepId={id}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FunnelPagesMenu;
